import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subject, Subscription, interval, switchMap } from 'rxjs';
import { parseAndDecodeCookieAccessToken } from 'src/app/auth/cookie';
import { environment as env } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private cookieService = inject(CookieService);
  private jwt = inject(JwtHelperService);

  private alertSource = new Subject();
  alert$ = this.alertSource.asObservable();
  private permission: NotificationPermission = 'default';
  private subscription: Subscription | null = null;
  private userInfo: any;
  private baseUrl: string = env.apiUrl;

  constructor(private http: HttpClient) {

  }

  showAlert(type: string, message: string, summary?: string) {
    setTimeout(() => {
      const actualSummary =
        summary || type.charAt(0).toUpperCase() + type.slice(1);
      this.alertSource.next({ type, summary: actualSummary, message });
    });
  }

  requestPermissionIfNeeded(): Promise<NotificationPermission> {
    if (this.permission === 'denied') {
      return Promise.resolve(this.permission);
    } else {
      return this.requestPermission();
    }
  }

  private requestPermission(): Promise<NotificationPermission> {
    return new Promise((resolve, reject) => {
      Notification.requestPermission()
        .then((perm) => {
          this.permission = Notification.permission;
          resolve(this.permission);
        })
        .catch((error) => {
          console.error('Error requesting notification permission:', error);
          reject(error);
        });
    });
  }

  public showNotification(title: string, body: string): void {
    if (
      Notification.permission === 'granted' &&
      localStorage.getItem('notificationsEnabled') === 'true'
    ) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, {
          body: body,
          icon: 'assets/logo-notification.png',
          data: { url: env.baseUrl },
        });
      });
    }
  }

  async getPermission(): Promise<NotificationPermission> {
    return new Promise((resolve) => {
      resolve(Notification.permission);
    });
  }

  startSessionNotifications(): void {
    this.userInfo = parseAndDecodeCookieAccessToken(
      this.cookieService,
      this.jwt
    );
    if (this.subscription) this.stopSessionNotifications();
    if (
      this.userInfo &&
      this.userInfo.rName &&
      this.userInfo.rName !== 'admin'
    ) {
      if (!this.subscription) {
        this.checkAndNotifyNextSession();
        this.subscription = interval(60 * 5 * 1000) // 5 minutos
          .pipe(switchMap(() => this.checkAndNotifyNextSession()))
          .subscribe({
            next: () => {},
            error: (error) => {},
          });
      }
    }
  }

  private checkAndNotifyNextSession(): Promise<void> {
    return new Promise((resolve) => {
      const userId = this.userInfo?.id;

      if (!userId) {
        return resolve();
      }

      this.getTodayClasses(userId).subscribe({
        next: (response) => {
          const now = new Date();
          const alertSentSessionId = localStorage.getItem('alertSentSessionId');

          response.forEach((session: any) => {
            const sessionTime = new Date();
            const [hours, minutes, seconds] = session.session_start.split(':');
            sessionTime.setHours(hours, minutes, seconds, 0);

            const timeDiff = (sessionTime.getTime() - now.getTime()) / 60000;

            if (session.id.toString() !== alertSentSessionId) {
              localStorage.removeItem('alertSent');
            }

            if (
              timeDiff <= 10 &&
              timeDiff > 0 &&
              (session.id.toString() !== alertSentSessionId ||
                !alertSentSessionId) &&
              !localStorage.getItem('alertSent')
            ) {
              const title = 'Upcoming Class!';
              const body = 'Your class will start shortly. Please get ready.';
              this.showNotification(title, body);
              localStorage.setItem('alertSentSessionId', session.id.toString());
              localStorage.setItem('alertSent', 'true');
            }
          });

          resolve();
        },
        error: (err) => {
          console.error('Error fetching classes', err);
          resolve();
        },
      });
    });
  }

  stopSessionNotifications(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
      localStorage.removeItem('alertSent');
    }
  }

  getTodayClasses(userId: number): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/student_sessions/today/${userId}`
    );
  }
}
